import { useState, useEffect, useMemo } from "react";
import { View, ScrollView, StyleSheet, TextInput, Platform, Pressable, ActivityIndicator, Alert } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ModernCard } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DateInput } from "@/components/ui/DateInput";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import Avatar from "@/components/Avatar";
import { PhotoCapture, PhotoFile, photoFileToBase64 } from "@/components/PhotoCapture";
import { shiftsService } from "@/services/shiftService";
import { ShiftType } from "@/types/db/shifts";
import { supabase } from "@/lib/supabase-client";

import { ShiftUser } from "@/types/db/shift_users";

interface ShiftExpenseScreenProps {
  shiftId: string;
  showOnBehalfOf?: boolean;
}

export default function ShiftExpenseScreen({ shiftId, showOnBehalfOf = true }: ShiftExpenseScreenProps) {
  const { colors } = useTheme();

  // Shift data
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState<ShiftType | null>(null);

  // Form state
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<PhotoFile[]>([]);

  // On behalf of
  const [isOnBehalf, setIsOnBehalf] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [rosterSearchQuery, setRosterSearchQuery] = useState("");

  // Fetch shift data
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!shiftId) return;
      setLoading(true);
      try {
        const row = (await shiftsService.getShiftById(shiftId)) as ShiftType | null;
        if (!alive) return;
        setShift(row);
      } catch (e) {
        console.error("Failed to load shift:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId]);

  // Get accepted users from shift positions
  const acceptedUsers = useMemo(() => {
    if (!shift?.shift_positions) return [];
    const users: ShiftUser[] = [];
    shift.shift_positions.forEach((position) => {
      (position.shift_users || []).forEach((su) => {
        if (su.status === "ACCEPTED") {
          users.push(su as ShiftUser);
        }
      });
    });
    return users;
  }, [shift?.shift_positions]);

  // Filter roster by search
  const filteredRosterUsers = useMemo(() => {
    if (!rosterSearchQuery.trim()) return acceptedUsers;
    const q = rosterSearchQuery.toLowerCase();
    return acceptedUsers.filter((su) => {
      const u = su.user;
      if (!u) return false;
      const name =
        `${u.preferred_name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.email_address || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [acceptedUsers, rosterSearchQuery]);

  const getDisplayName = (u: ShiftUser["user"]) => {
    if (!u) return "Unknown User";
    return u.preferred_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email_address || "Unknown User";
  };

  const toggleUserSelection = (id: string) => {
    setSelectedUserId((prev) => (prev === id ? null : id));
  };

  const handleAmountChange = (text: string) => {
    // Allow digits and a single decimal point
    const cleaned = text.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) return;
    setAmount(cleaned);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      Alert.alert("Invalid Amount", "Please enter a valid expense amount.");
      return;
    }

    setSubmitting(true);
    try {
      const photoData = photos.length > 0 ? await Promise.all(photos.map(photoFileToBase64)) : undefined;

      const { data, error } = await supabase.functions.invoke("submit-expense", {
        body: {
          shift_id: shiftId,
          amount: parsedAmount,
          category: category || undefined,
          description: description || undefined,
          date: date.toISOString(),
          notes: notes || undefined,
          on_behalf_of: isOnBehalf ? selectedUserId : undefined,
          photos: photoData,
        },
      });

      if (error) {
        console.error("[EXPENSE] Submit error:", error);
        Alert.alert("Error", error.message || "Failed to submit expense.");
        return;
      }

      if (data?.success) {
        Alert.alert("Expense Submitted", `Expense ${data.expense_number} created.`, [
          { text: "OK", onPress: () => router.dismiss() },
        ]);
      } else {
        Alert.alert("Error", data?.error || "Unexpected response from server.");
      }
    } catch (e: any) {
      console.error("[EXPENSE] Submit exception:", e);
      Alert.alert("Error", e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    router.dismiss();
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* Section 1: Amount & Category */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="cash" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Amount
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Enter the expense amount and an optional category.
        </ThemedText>
        <View style={{ gap: 12 }}>
          <View style={styles.fieldRow}>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.fieldLabel}>Amount *</ThemedText>
              <TextInput
                style={[
                  styles.fieldInput,
                  {
                    color: colors.text,
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  },
                ]}
                value={amount}
                onChangeText={handleAmountChange}
                placeholder="0.00"
                placeholderTextColor={colors.placeholder}
                keyboardType="decimal-pad"
              />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText style={styles.fieldLabel}>Category</ThemedText>
              <TextInput
                style={[
                  styles.fieldInput,
                  {
                    color: colors.text,
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  },
                ]}
                value={category}
                onChangeText={setCategory}
                placeholder="e.g. Travel, Supplies"
                placeholderTextColor={colors.placeholder}
              />
            </View>
          </View>
        </View>
      </ModernCard>

      {/* Section 2: On Behalf Of */}
      {showOnBehalfOf && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="account-switch" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              On Behalf Of
            </ThemedText>
          </View>
          <View style={styles.toggleRow}>
            <ThemedText style={styles.toggleLabel}>Submitting for someone else?</ThemedText>
            <ToggleSwitch
              value={isOnBehalf}
              onValueChange={(val) => {
                setIsOnBehalf(val);
                if (!val) {
                  setSelectedUserId(null);
                  setRosterSearchQuery("");
                }
              }}
            />
          </View>
          {isOnBehalf && (
            <View style={styles.userSection}>
              <TextInput
                style={[
                  styles.searchInput,
                  { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
                ]}
                placeholder="Search crew members..."
                placeholderTextColor={colors.placeholder}
                value={rosterSearchQuery}
                onChangeText={setRosterSearchQuery}
              />
              {filteredRosterUsers.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="account-search" size={32} color={colors.placeholder} />
                  <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                    {rosterSearchQuery ? "No matching crew members" : "No accepted crew members"}
                  </ThemedText>
                </View>
              ) : (
                <View style={styles.userList}>
                  {filteredRosterUsers.map((su) => {
                    const isSelected = selectedUserId === su.id;
                    return (
                      <Pressable
                        key={su.id}
                        onPress={() => toggleUserSelection(su.id)}
                        style={({ pressed, hovered }) => [
                          styles.userItem,
                          {
                            backgroundColor: isSelected ? colors.primary + "15" : colors.background,
                            borderColor: isSelected ? colors.primary : colors.border,
                          },
                          hovered && !isSelected && { backgroundColor: colors.card },
                          pressed && { opacity: 0.7 },
                        ]}
                      >
                        <Avatar
                          userId={su.user?.id}
                          firstName={su.user?.first_name}
                          lastName={su.user?.last_name}
                          size={36}
                        />
                        <View style={styles.userInfo}>
                          <ThemedText style={styles.userName}>{getDisplayName(su.user)}</ThemedText>
                        </View>
                        <View
                          style={[
                            styles.checkbox,
                            {
                              backgroundColor: isSelected ? colors.primary : "transparent",
                              borderColor: isSelected ? colors.primary : colors.border,
                            },
                          ]}
                        >
                          {isSelected && <MaterialCommunityIcons name="check" size={14} color="white" />}
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              )}
              {selectedUserId && (
                <View style={[styles.selectedCount, { backgroundColor: colors.primary + "15" }]}>
                  <MaterialCommunityIcons name="account-check" size={16} color={colors.primary} />
                  <ThemedText style={{ color: colors.primary, fontSize: 13 }}>
                    {getDisplayName(acceptedUsers.find((u) => u.id === selectedUserId)?.user)} selected
                  </ThemedText>
                </View>
              )}
            </View>
          )}
        </ModernCard>
      )}

      {/* Section 3: Date */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="calendar" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Expense Date
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          When was this expense incurred?
        </ThemedText>
        <DateInput value={date} onChange={setDate} mode="date" label="Date" />
      </ModernCard>

      {/* Section 3: Description */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="text-box-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Description
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Describe what this expense is for.
        </ThemedText>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          value={description}
          onChangeText={setDescription}
          placeholder="What was purchased or paid for..."
          placeholderTextColor={colors.placeholder}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* Section 4: Receipt Photo */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="camera" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Receipt
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Upload a photo of the receipt if available.
        </ThemedText>
        <PhotoCapture photos={photos} onPhotosChange={setPhotos} maxPhotos={3} placeholder="Tap to add receipt photo" />
      </ModernCard>

      {/* Section 5: Notes */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="note-text" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Notes
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Any additional notes for this expense.
        </ThemedText>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          value={notes}
          onChangeText={setNotes}
          placeholder="Optional notes..."
          placeholderTextColor={colors.placeholder}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* Action Buttons */}
      <View style={styles.buttonRow}>
        <Button text="Cancel" icon="close" iconPosition="left" onPress={handleCancel} style={styles.button} />
        <Button
          text={submitting ? "Submitting..." : "Submit Expense"}
          icon="check"
          iconPosition="left"
          onPress={handleSubmit}
          disabled={submitting}
          style={[styles.button, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  card: {},
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
  userSection: {
    marginTop: 16,
    gap: 12,
  },
  searchInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  userList: {
    gap: 8,
  },
  userItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: "500",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedCount: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
    borderRadius: 8,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  fieldRow: {
    flexDirection: "row",
    gap: 12,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 6,
  },
  fieldInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  notesInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    height: 48,
  },
});
